import { useState, useEffect, useRef } from "react"
import { X, ChevronDown, Pencil, Bold, Italic, Underline, Link, Image, Code, Mail } from "lucide-react"
import { sendEmail } from "../services/emailService"
import toast from "react-hot-toast"

const ff = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const divider = "1px solid #f3f4f6"
const DOMAIN = "@resolut.co.in"
const SUPPORT_EMAIL = "support@resolut.co.in"
const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "company"

const useOutsideClick = (cb) => {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb() }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [cb])
  return ref
}

const ToolbarButton = ({ title, onClick, children, active }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick?.() }}
    style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 28, height: 28, border: "none", borderRadius: 5,
      background: active ? "#f3f4f6" : "none",
      color: active ? "#111827" : "#374151",
      cursor: "pointer", flexShrink: 0, transition: "background 0.1s",
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f3f4f6" }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "none" }}
  >
    {children}
  </button>
)

const ColorSwatch = ({ color, onSelect }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onSelect(color) }}
    style={{
      width: 16, height: 16, borderRadius: 3,
      background: color, border: "1px solid rgba(0,0,0,0.12)",
      cursor: "pointer", flexShrink: 0,
    }}
  />
)

const FONT_COLORS = ["#111827", "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899"]

const EditorToolbar = ({ editorRef, isSending }) => {
  const [colorOpen, setColorOpen] = useState(false)
  const [activeFormats, setActiveFormats] = useState({})
  const colorRef = useOutsideClick(() => setColorOpen(false))

  useEffect(() => {
    const update = () => setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    })
    document.addEventListener("selectionchange", update)
    return () => document.removeEventListener("selectionchange", update)
  }, [])

  const exec = (cmd, val) => { editorRef.current?.focus(); document.execCommand(cmd, false, val) }
  const handleLink = () => { const url = prompt("Enter URL:"); if (url) exec("createLink", url.startsWith("http") ? url : `https://${url}`) }
  const handleImage = () => { const url = prompt("Enter image URL:"); if (url) exec("insertImage", url) }

  if (isSending) return null

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 1, padding: "6px 14px", borderBottom: divider, background: "#fafafa", flexWrap: "wrap" }}>
      <ToolbarButton title="Bold (⌘B)" onClick={() => exec("bold")} active={activeFormats.bold}><Bold size={13} strokeWidth={2.5} /></ToolbarButton>
      <ToolbarButton title="Italic (⌘I)" onClick={() => exec("italic")} active={activeFormats.italic}><Italic size={13} strokeWidth={2} /></ToolbarButton>
      <ToolbarButton title="Underline (⌘U)" onClick={() => exec("underline")} active={activeFormats.underline}><Underline size={13} strokeWidth={2} /></ToolbarButton>

      <div ref={colorRef} style={{ position: "relative", display: "flex" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ToolbarButton title="Font color" onClick={() => setColorOpen(v => !v)}>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, color: "#374151", fontFamily: "serif" }}>A</span>
              <span style={{ width: 12, height: 3, borderRadius: 1, background: "#111827" }} />
            </span>
          </ToolbarButton>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); setColorOpen(v => !v) }} style={{ width: 14, height: 28, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
            <ChevronDown size={9} strokeWidth={2.5} color="#9ca3af" />
          </button>
        </div>
        {colorOpen && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", padding: "8px", zIndex: 600, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
            {FONT_COLORS.map(c => <ColorSwatch key={c} color={c} onSelect={(col) => { exec("foreColor", col); setColorOpen(false) }} />)}
          </div>
        )}
      </div>

      <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px", flexShrink: 0 }} />
      <ToolbarButton title="Link" onClick={handleLink}><Link size={13} strokeWidth={2} /></ToolbarButton>
      <ToolbarButton title="Image" onClick={handleImage}><Image size={13} strokeWidth={2} /></ToolbarButton>
      <ToolbarButton title="Code" onClick={() => exec("formatBlock", "pre")}><Code size={13} strokeWidth={2} /></ToolbarButton>
      <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px", flexShrink: 0 }} />
      <ToolbarButton title="More options" onClick={() => {}}>
        <span style={{ display: "flex", gap: 2 }}>
          {[0, 1, 2].map(i => <span key={i} style={{ width: 3, height: 3, borderRadius: "50%", background: "#6b7280", display: "block" }} />)}
        </span>
      </ToolbarButton>
    </div>
  )
}

const SupportEmailModal = ({ isOpen, onClose, companyName = "", recipientName = "Resolut Support", defaultSubject = "" }) => {
  const [isEditingFrom, setIsEditingFrom] = useState(false)
  const [fromPrefix, setFromPrefix] = useState("company")
  const [fromDraft, setFromDraft] = useState("company")
  const [subject, setSubject] = useState(defaultSubject)
  const [message, setMessage] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [visible, setVisible] = useState(false)

  const editorRef = useRef(null)
  const subjectRef = useRef(null)
  const fromInputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      const prefix = slugify(companyName)
      setFromPrefix(prefix)
      setFromDraft(prefix)
      setIsEditingFrom(false)
    }
  }, [isOpen, companyName])

  useEffect(() => {
    if (isOpen) {
      const t = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(t)
    } else {
      setVisible(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      if (defaultSubject) setSubject(defaultSubject)
      setTimeout(() => subjectRef.current?.focus(), 120)
    }
  }, [isOpen, defaultSubject])

  useEffect(() => {
    if (isEditingFrom) setTimeout(() => fromInputRef.current?.focus(), 50)
  }, [isEditingFrom])

  const getTextContent = (html) => {
    const div = document.createElement("div")
    div.innerHTML = html
    return div.textContent || div.innerText || ""
  }

  const canSend = !isSending && subject.trim().length > 0 && getTextContent(message).trim().length > 0

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === "Escape" && !isSending) {
        if (isEditingFrom) { setIsEditingFrom(false); setFromDraft(fromPrefix); return }
        handleClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); if (canSend) handleSend() }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isOpen, isSending, canSend, isEditingFrom, fromPrefix])

  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const handleFromSave = () => {
    const trimmed = fromDraft.trim().replace(/[^a-zA-Z0-9._+-]/g, "")
    if (trimmed) setFromPrefix(trimmed)
    else setFromDraft(fromPrefix)
    setIsEditingFrom(false)
  }

  const handleSend = async () => {
    if (!subject.trim()) { toast.error("Please enter a subject"); return }
    if (!getTextContent(message).trim()) { toast.error("Please enter a message"); return }
    setIsSending(true)
    try {
      await sendEmail({
        email: SUPPORT_EMAIL,
        subject: `Support: ${subject}`,
        text: getTextContent(message),
        html: message,
      })
      toast.success("Email sent successfully!")
      setSubject("")
      setMessage("")
      if (editorRef.current) editorRef.current.innerHTML = ""
      handleClose()
    } catch (err) {
      toast.error(err.message || "Failed to send email. Please try again.")
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        .sem-editor { font-family: ${ff}; }
        .sem-editor:empty::before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; }
        .sem-editor:focus { outline: none; }
        .sem-editor p { margin: 0.35em 0; }
        .sem-editor ul { margin: 0.4em 0; padding-left: 1.4em; list-style: disc; }
        .sem-editor ol { margin: 0.4em 0; padding-left: 1.4em; list-style: decimal; }
        .sem-editor li { display: list-item; margin: 0.15em 0; }
        .sem-close-btn:hover { color: #374151 !important; background: #f3f4f6 !important; }
        .sem-from-edit:hover { background: #f3f4f6 !important; }
        @keyframes sem-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={!isSending ? handleClose : undefined}
        style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.28)", opacity: visible ? 1 : 0, transition: "opacity 0.18s ease" }}
      />

      {/* Centering wrapper */}
      <div style={{ position: "fixed", inset: 0, zIndex: 201, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", pointerEvents: "none" }}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%", maxWidth: 620, background: "#fff", borderRadius: 10,
            border: "1px solid #e5e7eb", boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            fontFamily: ff, pointerEvents: "all",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0) scale(1)" : "translateY(6px) scale(0.99)",
            transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1)",
            overflow: "hidden",
          }}
        >
          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: divider, background: "#fff" }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.01em" }}>Send Email</p>
              <p style={{ fontSize: 13, color: "#374151", margin: "3px 0 0", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                to <span style={{ fontWeight: 600, color: "#111827" }}>{recipientName}</span>
                <span style={{ color: "#d1d5db" }}>·</span>
                <span style={{ color: "#6b7280", fontWeight: 500 }}>{SUPPORT_EMAIL}</span>
              </p>
            </div>
            <button onClick={handleClose} disabled={isSending} className="sem-close-btn" style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", borderRadius: 6, cursor: "pointer", color: "#6b7280", transition: "all 0.12s" }}>
              <X size={14} strokeWidth={2} />
            </button>
          </div>

          {/* ── From row ── */}
          <div style={{ display: "flex", alignItems: "center", padding: "0 20px", height: 46, borderBottom: divider }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", width: 76, flexShrink: 0 }}>From</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
              {isEditingFrom ? (
                <input
                  ref={fromInputRef}
                  value={fromDraft}
                  onChange={(e) => setFromDraft(e.target.value.replace(/[^a-zA-Z0-9._+-]/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleFromSave(); if (e.key === "Escape") { setIsEditingFrom(false); setFromDraft(fromPrefix) } }}
                  onBlur={handleFromSave}
                  style={{ border: "none", outline: "none", fontSize: 14, fontWeight: 500, color: "#111827", background: "transparent", fontFamily: ff, padding: 0, minWidth: 0, width: `${Math.max(fromDraft.length, 4)}ch` }}
                />
              ) : (
                <span style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{fromPrefix}</span>
              )}
              <span style={{ fontSize: 14, fontWeight: 500, color: "#6b7280", flexShrink: 0 }}>{DOMAIN}</span>
            </div>
            <button onClick={() => { setFromDraft(fromPrefix); setIsEditingFrom(true) }} className="sem-from-edit" style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", flexShrink: 0, background: "none", border: "none", borderRadius: 5, fontSize: 12.5, fontWeight: 600, color: "#6b7280", cursor: "pointer", fontFamily: ff, transition: "all 0.12s" }}>
              <Pencil size={11} strokeWidth={2} />
              Edit
            </button>
          </div>

          {/* ── Subject row ── */}
          <div style={{ display: "flex", alignItems: "center", padding: "0 20px", height: 46, borderBottom: divider }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", width: 76, flexShrink: 0 }}>Subject</span>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Write a subject…"
              disabled={isSending}
              style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontWeight: 500, color: "#111827", background: "transparent", fontFamily: ff, padding: 0 }}
            />
          </div>

          {/* ── Toolbar ── */}
          <EditorToolbar editorRef={editorRef} isSending={isSending} />

          {/* ── Editor ── */}
          <div
            ref={editorRef}
            contentEditable={!isSending}
            onInput={() => { if (editorRef.current) setMessage(editorRef.current.innerHTML) }}
            className="sem-editor"
            data-placeholder="Write your message…"
            suppressContentEditableWarning
            style={{ minHeight: 280, padding: "16px 20px", fontSize: 14, lineHeight: 1.75, color: "#111827", fontFamily: ff }}
          />

          {/* ── Footer ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "12px 20px", borderTop: divider, background: "#fff" }}>
            <button onClick={handleClose} disabled={isSending} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 7, padding: "7px 16px", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: ff, transition: "all 0.12s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = "#111827" }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#374151" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{ height: 36, padding: "0 20px", background: canSend ? "#111827" : "#f3f4f6", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, color: canSend ? "#fff" : "#9ca3af", cursor: canSend ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, fontFamily: ff, letterSpacing: "-0.01em", transition: "background 0.12s, color 0.12s" }}
              onMouseEnter={(e) => { if (canSend) e.currentTarget.style.background = "#27272a" }}
              onMouseLeave={(e) => { if (canSend) e.currentTarget.style.background = "#111827" }}
            >
              {isSending ? (
                <>
                  <div style={{ width: 12, height: 12, border: "1.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "sem-spin 0.7s linear infinite" }} />
                  Sending…
                </>
              ) : (
                <>
                  <Mail size={13} strokeWidth={2} />
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default SupportEmailModal
